import {SIGNATURE_DECLARATION} from '../utils/constants';

/**
 * This function fetch a notary with a given ID from remote server.
 *
 * @param {string} personId - The personId.
 */
export async function getPersonById(personId: string) {
  return fetch('/notaries-public/' + personId, {
    method: 'GET'
  }).then((response) => {
    return response.json();
  });
}

/**
 * Uploads a contract file for a notary.
 *
 * @param {string} contractType - The contract type
 * @param {File} file - The file to upload.
 * @param {number} notaryId - The notary id.
 * @param {boolean} profileMode - mode for profile or notary edit view.
 */
export async function addContract(
  contractType: string,
  file: File,
  notaryId: number,
  profileMode: Boolean
) {
  const data = new FormData();
  data.append('file', file);
  return fetch(getAddContractUrl(contractType, notaryId, profileMode), {
    method: 'POST',
    body: data
  })
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      return {errorMessage: error};
    });
}

/**
 * Get the url depending on contracttype
 * @param {string} contractType
 * @param {number} notaryId - The notary id.
 * @param {boolean} profileMode - mode for profile or notary edit view.
 * @return {string}
 */
function getAddContractUrl(
  contractType: string,
  notaryId: number,
  profileMode: Boolean
) {
  if (contractType === SIGNATURE_DECLARATION && profileMode) {
    return '/profile-update/contracts';
  } else if (contractType === SIGNATURE_DECLARATION && !profileMode) {
    return '/notaries/' + notaryId + '/signature-declaration';
  }
  return '/notaries/' + notaryId + '/contracts/' + contractType;
}

/**
 * Delete a contract file for a notary.
 *
 * @param {string} contractType - The contract type
 * @param {number} notaryId - The notary id.
 */
export async function deleteContract(contractType: string, notaryId: number) {
  return fetch('/notaries/' + notaryId + '/contracts/' + contractType, {
    method: 'DELETE'
  })
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      return {errorMessage: error};
    });
}

/**
 * This function updates a notary with a given ID.
 *
 * @param {object} personData - The person data.
 * @param {string} personId - The personId.
 */
export async function updatePerson(personData: Object, personId: string) {
  return fetch('/notaries/' + personId, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(personData)
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function updates a notary profile.
 *
 * @param {object} profileData - The person data.
 */
export async function updateProfle(profileData: Object) {
  return fetch('/profile-update/', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(profileData)
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function updates a function with a given ID.
 *
 * @param {object} functionData - The function data.
 * @param {string} personId - The personId.
 */
export async function updateFunction(functionData: Object, personId: string) {
  return fetch('/notaries/' + personId + '/functions', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(functionData)
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function fetch notaries by canton name from remote server.
 *
 * @param {string} canton - The name of the canton.
 */
export async function getPersonsByCanton(canton: string) {
  return fetch('/notaries-public/canton/' + canton, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function fetch employes from remote server.
 *
 */
export async function getEmployees() {
  return fetch('/employees/', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function fetch persons from remote server.
 *
 */
export async function getPersons() {
  return fetch('/persons/', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function fetch credentials for a person from remote server.
 *
 * @param {string} personId
 */
export async function getCredentials(personId: string) {
  return fetch('/persons/' + personId + '/credentials', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function fetch functions for a person from remote server.
 *
 * @param {string} personId
 */
export async function getFunctions(personId: string) {
  return fetch('/notaries/' + personId + '/functions', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function fetch the successor for a person from remote server.
 *
 * @param {string} personId
 */
export async function getSuccessor(personId: string) {
  return fetch('/notaries-public/' + personId + '/successor', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function save the successor for a person.
 *
 * @param {string} personId
 * @param {Object} successor
 */
export async function saveSuccessor(personId: string, successor: Object) {
  return fetch('/notaries/' + personId + '/successor', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(successor)
  }).then((response) => {
    return response.json();
  });
}

/**
 * Delete a credential with given id.
 *
 * @param {string} personId
 * @param {string} credentialId
 *
 * @return {Object} respone from delete action.
 */
export function deleteCredential(personId: string, credentialId: string) {
  return fetch('/persons/' + personId + '/credentials/' + credentialId, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function save a employee.
 *
 * @param {Object} employeeData
 */
export async function saveEmployee(employeeData) {
  const url = '/employees/' + (employeeData.id ? employeeData.id : '');
  return fetch(url, {
    method: employeeData.id ? 'PUT' : 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(employeeData)
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function delete a employee.
 *
 * @param {string} employeeId
 */
export async function deleteEmployee(employeeId) {
  return fetch('/employees/' + employeeId, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function fetch notary applications from remote server.
 *
 */
export async function getNotaryApplications() {
  return fetch('/persons/applications', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    return response.json();
  });
}

/**
 * This function fetch notary seal api (credential pair) from remote server.
 *
 */
export async function getNotarySealApi() {
  return fetch('/api-management/seal/', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
    return response.json();
  });
}

/**
 * This function generates a new API (credential pair).
 *
 */
export async function createNotarySealApi() {
  return fetch('/api-management/seal/', {
    credentials: 'same-origin',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then((response) => {
    if (!response.ok) {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
    return response.json();
  });
}
